import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
// Non-sensitive data only (public catalog: product IDs, names, prices) — localStorage is the
// correct, intentional storage choice. No PII, no auth tokens.
const STORAGE_KEY = "monolith.cart.v1";

export const CartProvider = ({ children }) => {
    const [items, setItems] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });
    const [open, setOpen] = useState(false);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }, [items]);

    const addItem = useCallback((product, size, color = null, qty = 1) => {
        setItems((prev) => {
            const key = `${product.id}-${size}-${color || "_"}`;
            const existing = prev.find((i) => i.key === key);
            if (existing) {
                return prev.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i));
            }
            // Pick the colour-specific hero image if a colour was selected
            const colourMatch = color && (product.colors || []).find((c) => c.name === color);
            return [
                ...prev,
                {
                    key,
                    id: product.id,
                    slug: product.slug,
                    name: product.name,
                    price: product.price,
                    image: colourMatch?.image || product.images?.[0],
                    size,
                    color,
                    qty,
                },
            ];
        });
        setOpen(true);
    }, []);

    const removeItem = useCallback(
        (key) => setItems((prev) => prev.filter((i) => i.key !== key)),
        []
    );
    const updateQty = useCallback(
        (key, qty) =>
            setItems((prev) =>
                prev.map((i) => (i.key === key ? { ...i, qty: Math.max(1, qty) } : i))
            ),
        []
    );
    const clear = useCallback(() => setItems([]), []);

    const subtotal = useMemo(
        () => items.reduce((sum, i) => sum + i.price * i.qty, 0),
        [items]
    );
    const count = useMemo(() => items.reduce((sum, i) => sum + i.qty, 0), [items]);

    const value = useMemo(
        () => ({ items, addItem, removeItem, updateQty, clear, subtotal, count, open, setOpen }),
        [items, addItem, removeItem, updateQty, clear, subtotal, count, open]
    );

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used within CartProvider");
    return ctx;
};
